const { getFocusableElements } = window.BAO.Utils

class SiteModal extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['backdrop']
  }

  connectedCallback () {
    super.connectedCallback()
    this.close = this.close.bind(this)
    this.open = this.open.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleTabPress = this.handleTabPress.bind(this)

    /** @type {HTMLElement} */
    this.elementUsedToOpenModal = null
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      document.body,
      'site-modal:open',
      this.handleSiteModalOpen.bind(this),
    )
    this.listeners.add(
      document.body,
      'site-modal:close',
      this.handleSiteModalClose.bind(this),
    )

    this.listeners.add(this.els.backdrop.element, 'click', () => this.close())

    this.setupCloseListeners()

    this.externalToggles.forEach(toggle => {
      this.listeners.add(toggle, 'click', () => this.open())
    })
  }

  open () {
    this.isOpen = true
  }

  close () {
    this.isOpen = false
  }

  /** @param {CustomEvent<{ id: string; }>} event */
  handleSiteModalOpen (event) {
    if (event.detail.id === this.id) {
      this.dispatchWishlistEvent(event.target)
      this.open()

      this.externalEls?.popover?.elements.forEach(el => {
        if (typeof el.hidePopover === 'function') {
          el.hidePopover()
        }
      })
    }
  }

  dispatchWishlistEvent (target) {
    if (target.dataset.variantInfo) {
      this.dispatchEvent(
        new CustomEvent('wishlist-variant-info:triggered', {
          detail: {
            id: target.dataset.variantInfo,
          },
          bubbles: true,
        }),
      )
    }
  }

  /** @param {CustomEvent<{ id: string; }>} event */
  handleSiteModalClose (event) {
    if (event.detail.id === this.id) {
      this.close()
    }
  }

  /** @param {KeyboardEvent} event */
  handleKeydown (event) {
    /** @type {Record<string, (event: KeyboardEvent) => void} */
    const shortcuts = {
      Tab: this.handleTabPress,
      Escape: this.close,
    }

    const action = shortcuts[event.key]

    if (!action) {
      return
    }

    action(event)
  }

  /** @param {KeyboardEvent} event */
  handleTabPress (event) {
    const {
      0: first,
      [this.focusableElements.length - 1]: last,
    } = this.focusableElements

    const shouldLoopToFirstElement = !event.shiftKey && event.target === last
    const shouldLoopToLastElement = event.shiftKey && event.target === first
    const shouldOverrideDefault =
      shouldLoopToFirstElement || shouldLoopToLastElement

    if (shouldOverrideDefault) {
      event.preventDefault()
    }

    if (shouldLoopToFirstElement) {
      first.focus()
    } else if (shouldLoopToLastElement) {
      last.focus()
    }
  }

  setupCloseListeners () {
    if (this.els.close) {
      this.els.close.elements.forEach(button => {
        this.listeners.add(button, 'click', () => this.close())
      })
    }
  }

  get isOpen () {
    return this.getAttribute('aria-hidden') === 'false'
  }

  set isOpen (isOpen) {
    this.setAttribute('aria-hidden', !isOpen)

    if (this.hasAttribute('data-lock-scroll')) {
      document.body.classList.toggle('util-SiteModalOverflowed', isOpen)
    }

    if (isOpen) {
      this.elementUsedToOpenModal = document.activeElement

      this.dispatchEvent(
        new CustomEvent('site-modal:opened', { bubbles: true }),
      )

      window.setTimeout(() => {
        this.els.close?.element.focus()

        this.listeners.add(document.body, 'keydown', this.handleKeydown)
      }, 200)
    } else {
      this.listeners.remove('keydown', this.handleKeydown)

      this.elementUsedToOpenModal?.focus()
      this.elementUsedToOpenModal = null

      this.dispatchEvent(
        new CustomEvent('site-modal:closed', { bubbles: true }),
      )

      this.dispatchEvent(
        new CustomEvent(`${this.id}:closed`, { bubbles: true }),
      )
    }
  }

  /** @returns {HTMLButtonElement[]} */
  get externalToggles () {
    return (
      this.externalEls.toggle?.elements.filter(
        toggle => toggle.dataset.siteModalId === this.id,
      ) ?? []
    )
  }

  get focusableElements () {
    return getFocusableElements(this)
  }
}

if (!customElements.get('site-modal')) {
  customElements.define('site-modal', SiteModal)
}

class SiteModalToggle extends window.BAO.CustomElement({
  SuperClass: HTMLAnchorElement,
}) {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick.bind(this))
  }

  /** @param {PointerEvent} event  */
  handleClick (event) {
    event.preventDefault()

    this.requestModalOpen()
  }

  requestModalOpen () {
    this.dispatchEvent(
      new CustomEvent('site-modal:open', {
        bubbles: true,
        detail: { id: this.getAttribute('site-modal-id') },
      }),
    )
  }
}

if (!window.customElements.get('site-modal-toggle')) {
  window.customElements.define('site-modal-toggle', SiteModalToggle, {
    extends: 'a',
  })
}
